import { Changeset } from 'ember-changeset';
import { BufferedChangeset } from 'ember-changeset/types';

import { restartableTask, task } from 'ember-concurrency';
import IntlService from 'ember-intl/services/intl';
import Transaction, {
  Queries,
  TransactionResourceName
} from 'volta/models/transaction';
import StoreService from 'volta/services/-store';
import BvFlashService from 'volta/services/bv-flash';
import { commonOrNull } from 'volta/utils/array-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs {
  onClose: () => void;
  transactions?: Transaction[];
}

export default class TransactionsUpdateModal extends Component<IArgs> {
  // Services
  // ~~~~

  @service intl!: IntlService;
  @service store!: StoreService;
  @service bvFlash!: BvFlashService;

  // Properties
  // ~~~~

  @tracked orderTypes = [];
  @tracked values = {
    orderType: null
  };

  @tracked changeset!: BufferedChangeset;

  // Actions
  // ~~~~~

  @action
  handleDidInsert() {
    this.orderTypesTask.perform();
    this.initValues(this.args.transactions);
  }

  @action
  handleSubmit() {
    if (this.changeset.isValid) {
      this.changeset.execute();
      const { transactions = [] } = this.args;
      const ids = transactions.map((t) => t.id);
      this.submitTask.perform({ ids, ...this.values });
    }
  }

  // Helpers
  // ~~~~~

  initValues(transactions: Transaction[] = []) {
    this.values = {
      orderType: commonOrNull(transactions.map((t) => t.orderType)) as null
    };
    this.changeset = Changeset(this.values);
  }

  submitTask = task(async (cmd: { ids: string[]; orderType: string | null }) => {
    try {
      const tx = await Transaction.updateManyTransactions(cmd);

      this.bvFlash.success(
        this.intl.t('bvList.bulkActions.updateSuccess', {
          count: tx.length,
          resourceNameSingular: this.intl.t(TransactionResourceName.singular),
          resourceNamePlural: this.intl.t(TransactionResourceName.plural)
        })
      );
      this.args.onClose?.();
      return tx;
    } catch (error) {
      defaultErrorHandler(error);
      return this.bvFlash.apiError();
    }
  });

  orderTypesTask = restartableTask(async () => {
    try {
      const response = await this.store.customQuery('transaction', Queries.OrderTypes);
      this.orderTypes = response;
      return response;
    } catch (error) {
      defaultErrorHandler(error);
      return;
    }
  });
}
